import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

const schema = z.object({
  display_name: z.string().trim().min(1).max(120),
  kind: z.enum(['cashier', 'consulting_room', 'admin', 'mixed']),
});

export type WorkstationFormValues = z.infer<typeof schema>;

interface WorkstationFormDialogProps {
  title: string;
  initial_values?: Partial<WorkstationFormValues>;
  submitting: boolean;
  onSubmit: (values: WorkstationFormValues) => Promise<void>;
  onCancel: () => void;
}

export function WorkstationFormDialog({
  title,
  initial_values,
  submitting,
  onSubmit,
  onCancel,
}: WorkstationFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const form = useForm<WorkstationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      display_name: initial_values?.display_name ?? '',
      kind: initial_values?.kind ?? 'consulting_room',
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit((values) => onSubmit(values))(event);
          }}
          noValidate
        >
          <CardHeader>
            <CardTitle className="font-display font-bold tracking-tight">{title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ws_display_name">{t('workstation.display_name_label')}</Label>
              <Input
                id="ws_display_name"
                maxLength={120}
                {...form.register('display_name')}
              />
              {form.formState.errors.display_name ? (
                <p className="text-sm text-destructive">
                  {form.formState.errors.display_name.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="ws_kind">{t('workstation.kind_label')}</Label>
              <select
                id="ws_kind"
                {...form.register('kind')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="cashier">{t('workstation.kind_cashier')}</option>
                <option value="consulting_room">{t('workstation.kind_consulting_room')}</option>
                <option value="admin">{t('workstation.kind_admin')}</option>
                <option value="mixed">{t('workstation.kind_mixed')}</option>
              </select>
            </div>
          </CardContent>
          <CardFooter className="justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('common.loading') : t('common.save')}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
